import { useEffect, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Box, Button, Container, Typography } from '@mui/material';
import { removeAllWhitespace } from '@npi/buddy-utils';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { navigate, routes } from '@redwoodjs/router';
import { MetaTags } from '@redwoodjs/web';

import { NPILogo } from 'src/assets/images';
import { VerificationSVG } from 'src/assets/images/verification';
import { FieldInput } from 'src/components/FormikComponents';
import OtpTimer from 'src/components/OtpTimer/OtpTimer';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { useAuthContext } from 'src/contexts/AuthProvider';
import {
    RESEND_OTP_MUTATION,
    VERIFY_USER_OTP_MUTATION,
} from 'src/graphql/mutations/login.mutation';

const VerificationPage = () => {
    const { setUser, setStoredAccessToken } = useAuthContext();

    const [isValid, setIsValid] = useState(true);

    const otpInputRef = useRef(null);

    const [verifyUserOtp] = useMutation(VERIFY_USER_OTP_MUTATION, {
        onCompleted: ({ verifyUserOtp }) => {
            if (verifyUserOtp.status) {
                setStoredAccessToken(`${verifyUserOtp.access_token}`);
                setUser(verifyUserOtp?.user);

                if (window.location.search.includes('redirectURI')) {
                    const params = new URLSearchParams(window.location.search);
                    const redirectURI = params.get('redirectURI');
                    navigate(redirectURI, { replace: true });
                } else {
                    navigate(routes.home(), { replace: true });
                }
            } else {
                showErrorToast('Invalid OTP. Please enter a valid OTP');
            }
        },
        onError: () => {
            showErrorToast('Invalid OTP. Please try again');
        },
    });

    useEffect(() => {
        const timeout = setTimeout(() => {
            otpInputRef.current.focus();
        }, 100);

        return () => {
            clearTimeout(timeout);
        };
    }, []);

    const [resendOtp] = useMutation(RESEND_OTP_MUTATION, {
        onCompleted: ({ resendOtp }) => {
            if (resendOtp) {
                showSuccessToast('OTP sent successfully');
                setIsValid(true);
            } else {
                showErrorToast('We could not send you a new OTP. Please try again later');
            }
        },
        onError: () => {
            showErrorToast('We could not send you a new OTP. Please try again later');
        },
    });

    const handleExpiredOtp = () => {
        handleResendOtp();
    };

    const handleResendOtp = () => {
        resendOtp();
        setIsValid(true);
    };

    const handleTimeout = () => {
        setIsValid(false);
    };

    const handleSubmitVerifyUserOTP = async ({ verificationCode }) => {
        await verifyUserOtp({
            variables: {
                otp: removeAllWhitespace(verificationCode),
            },
        });
    };

    return (
        <>
            <MetaTags title="Verification" description="Verification page" />

            <Formik
                initialValues={{ verificationCode: '' }}
                onSubmit={handleSubmitVerifyUserOTP}
                validationSchema={Yup.object().shape({
                    verificationCode: Yup.string()
                        .required('OTP is required')
                        .min(6, 'OTP must be 6 characters'),
                })}
            >
                {({ handleSubmit, isSubmitting }) => {
                    return (
                        <Container
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100vh',
                                width: '100vw',
                                justifyContent: 'center',
                            }}
                        >
                            <Box
                                width="100%"
                                display="flex"
                                borderRadius="20px"
                                boxShadow="0 0 25px rgba(56, 11, 11, 0.29)"
                                overflow="hidden"
                                sx={{ backgroundColor: 'white' }}
                                minHeight="560px"
                                padding="40px 0px"
                            >
                                <Box display="flex" flex={0.5} position="relative">
                                    <Box
                                        position="absolute"
                                        width="100%"
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="flex-center"
                                        justifyContent="center"
                                        padding="20px"
                                    >
                                        <Box
                                            display="flex"
                                            width="100%"
                                            justifyContent="center"
                                            sx={{
                                                objectFit: 'contain',
                                            }}
                                        >
                                            <VerificationSVG />
                                        </Box>
                                    </Box>
                                </Box>
                                <Box display="flex" flex={0.5} alignItems="flex-center">
                                    <Box
                                        maxWidth="70%"
                                        margin="0 auto"
                                        display="flex"
                                        justifyContent="flex-start"
                                        alignItems="center"
                                        flexDirection="column"
                                    >
                                        <form
                                            onSubmit={handleSubmit}
                                            style={{
                                                height: '100%',
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                gap="16px"
                                                width="100%"
                                                margin="0 auto"
                                            >
                                                <NPILogo />
                                                <Typography
                                                    fontWeight="500"
                                                    fontSize="24px"
                                                    color="black"
                                                >
                                                    OTP
                                                </Typography>
                                                <Typography fontSize="16px" color="#4d4d4d">
                                                    An OTP has been sent to the mobile number
                                                    registered to this user&apos;s account. The OTP
                                                    will expire in:
                                                </Typography>

                                                <OtpTimer
                                                    onTimeout={handleTimeout}
                                                    isValid={isValid}
                                                />

                                                {!isValid && (
                                                    <Typography fontSize="16px" color="#4d4d4d">
                                                        Your OTP has expired.
                                                        <Button
                                                            data-testid="resend-expired-otp-button"
                                                            variant="text"
                                                            sx={{
                                                                textTransform: 'none',
                                                                color: 'black',
                                                            }}
                                                            onClick={handleExpiredOtp}
                                                        >
                                                            Click here to resend
                                                        </Button>
                                                    </Typography>
                                                )}
                                                <FieldInput
                                                    name="verificationCode"
                                                    ref={otpInputRef}
                                                    placeholder="Enter OTP"
                                                />

                                                <Button
                                                    data-testid="otp-submit-button"
                                                    color="primary"
                                                    sx={{
                                                        bgcolor: '#000',
                                                        color: '#fff',
                                                        '&:hover': {
                                                            bgcolor: '#000',
                                                            color: '#fff',
                                                            opacity: 0.8,
                                                        },
                                                        '&:disabled': {
                                                            bgcolor: '#fafafa',
                                                            color: 'grey',
                                                            border: 'thin solid #ccc',
                                                        },
                                                    }}
                                                    type="submit"
                                                    disabled={!isValid}
                                                >
                                                    {isSubmitting ? 'Verifying...' : 'LOG IN'}
                                                </Button>
                                            </Box>
                                        </form>
                                    </Box>
                                </Box>
                            </Box>
                        </Container>
                    );
                }}
            </Formik>
        </>
    );
};

export default VerificationPage;
